import React, { FC, useState, useEffect } from 'react';
import { IVisitTemplate, IVisitSelection } from '../../types';

interface INotificationSettingsStepProps {
  visitTemplates: IVisitTemplate[];
  selectedVisitIndices: number[];
  visitSelections: Map<number, IVisitSelection>;
  onNotificationSettingsChange: (visitIndex: number, settings: IVisitSelection['notificationsSettings']) => void;
}

const NOTIFICATION_OPTIONS = [
  { value: 1440, label: '1 day before appointment' },
  { value: 2880, label: '2 days before appointment' },
  { value: 4320, label: '3 days before appointment' },
  { value: 10080, label: '1 week before appointment' },
  { value: 60, label: '1 hour before appointment' },
  { value: 120, label: '2 hours before appointment' },
  { value: 720, label: '12 hours before appointment' },
];

export const NotificationSettingsStep: FC<INotificationSettingsStepProps> = ({
  visitTemplates,
  selectedVisitIndices,
  visitSelections,
  onNotificationSettingsChange,
}) => {
  const [localSettings, setLocalSettings] = useState<Map<number, IVisitSelection['notificationsSettings']>>(new Map());

  // Initialize from visitSelections or template defaults AND sync to parent
  useEffect(() => {
    const initial = new Map<number, IVisitSelection['notificationsSettings']>();

    selectedVisitIndices.forEach((visitIndex) => {
      const selection = visitSelections.get(visitIndex);

      const visit = visitTemplates.find((v) => v.index === visitIndex);
      
      let settings: IVisitSelection['notificationsSettings'];
      
      if (selection?.notificationsSettings) {
        settings = selection.notificationsSettings;
      } else if (visit?.notificationsSettings) {
        settings = visit.notificationsSettings;
      } else {
        // Default settings
        settings = {
          types: { sms: true, email: true },
          first: 1440,
          second: 60,
        };
      }
      
      initial.set(visitIndex, settings);
      
      // Sync to parent if not already set
      if (!selection?.notificationsSettings) {
        onNotificationSettingsChange(visitIndex, settings);
      }
    });

    setLocalSettings(initial);
  }, [selectedVisitIndices, visitSelections, visitTemplates, onNotificationSettingsChange]);

  const handleTypeChange = (visitIndex: number, type: 'sms' | 'email', checked: boolean) => {
    const current = localSettings.get(visitIndex);

    if (!current) return;

    const updated = {
      ...current,
      types: {
        ...current.types,
        [type]: checked,
      },
    };

    setLocalSettings((prev) => new Map(prev).set(visitIndex, updated));
    onNotificationSettingsChange(visitIndex, updated);
  };

  const handleFirstChange = (visitIndex: number, value: number) => {
    const current = localSettings.get(visitIndex);

    if (!current) return;

    const updated = {
      ...current,
      first: value,
    };

    setLocalSettings((prev) => new Map(prev).set(visitIndex, updated));
    onNotificationSettingsChange(visitIndex, updated);
  };

  const handleSecondChange = (visitIndex: number, value: number | undefined) => {
    const current = localSettings.get(visitIndex);

    if (!current) return;

    const updated = {
      ...current,
      second: value,
    };

    setLocalSettings((prev) => new Map(prev).set(visitIndex, updated));
    onNotificationSettingsChange(visitIndex, updated);
  };

  return (
    <>
      <div className="multi-appointment-info-box">
        Please note that you can choose upon 2 variants of time for notifications
      </div>

      {selectedVisitIndices.map((visitIndex, orderIndex) => {
        const visit = visitTemplates.find((v) => v.index === visitIndex);

        const settings = localSettings.get(visitIndex);

        if (!visit || !settings) return null;

        return (
          <div key={visitIndex} className="multi-appointment-notification-card">
            <div className="multi-appointment-notification-header">
              <div className="multi-appointment-visit-num">{visit.index + 1}</div>
              <div className="multi-appointment-notification-title">
                Visit {visit.index + 1} — {visit.type || visit.name}
              </div>
            </div>

            <div className="multi-appointment-notification-body">
              <div className="multi-appointment-notification-section">
                <label className="field-label">Send notification using</label>
                <div className="multi-appointment-notification-checkboxes">
                  <label className="multi-appointment-checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.types.email}
                      onChange={(e) => handleTypeChange(visitIndex, 'email', e.target.checked)}
                    />
                    Email
                  </label>
                  <label className="multi-appointment-checkbox-label">
                    <input
                      type="checkbox"
                      checked={settings.types.sms}
                      onChange={(e) => handleTypeChange(visitIndex, 'sms', e.target.checked)}
                    />
                    SMS
                  </label>
                </div>
              </div>

              <div className="multi-appointment-notification-section">
                <label className="field-label">First notification</label>
                <select
                  className="input-control"
                  value={settings.first}
                  onChange={(e) => handleFirstChange(visitIndex, Number(e.target.value))}
                >
                  {NOTIFICATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="multi-appointment-notification-section">
                <label className="field-label">
                  Second notification <span style={{ color: '#667085', fontWeight: 400 }}>(optional)</span>
                </label>
                <select
                  className="input-control"
                  value={settings.second || ''}
                  onChange={(e) => handleSecondChange(visitIndex, e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">None</option>
                  {NOTIFICATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
